import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useCredits } from "@/hooks/useCredits";
import { hasToolAccess, PlanTier } from "@/config/pricing";
import {
  Megaphone,
  Video,
  Radio,
  PlayCircle,
  MessageSquare,
  FileText,
  ChevronDown,
  LogOut,
  User,
  CreditCard,
  BookOpen,
  Settings,
  Shield,
  HelpCircle,
  Gift,
  Zap,
  Lock,
  Users,
} from "lucide-react";
import storycraftLogo from "@/assets/storycraft-logo.png";
import nexisLogo from "@/assets/nexis-logo-new.png";
import monetizationLogo from "@/assets/monetization-logo-new.png";
import askfintLogo from "@/assets/askfint-logo-new.png";
import clipaiLogo from "@/assets/clipai-logo-new.png";
import keywordResearchLogo from "@/assets/keyword-research-logo-new.png";
import emailLogo from "@/assets/email-list-builder-logo-new.png";
import salesLogo from "@/assets/sales-page-builder-logo-new.png";
import launchLogo from "@/assets/launch-advisor-logo-new.png";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useState } from "react";
import { Progress } from "@/components/ui/progress";

const homeItems = [
  { title: "Announcements", url: "/app/announcements", icon: Megaphone, iconColor: "text-blue-400" },
  { title: "Live Calls", url: "/app/live-calls", icon: Video, iconColor: "text-purple-400" },
  { title: "Livestreams", url: "/app/livestreams", icon: Radio, iconColor: "text-pink-400" },
];

const learnItems = [
  { title: "From Audience to Income", url: "/app/storycraft-formula", icon: BookOpen, iconColor: "text-amber-400" },
  { title: "Tutorials", url: "/app/tutorials", icon: PlayCircle, iconColor: "text-green-400" },
  { title: "Call Recordings", url: "/app/recordings", icon: Video, iconColor: "text-red-400" },
];

const toolItems = [
  { title: "Nexis AI", url: "/app/nexis-ai", logo: nexisLogo, toolKey: "nexis-ai" },
  { title: "Monetisation AI", url: "/app/monetization-ai", logo: monetizationLogo, toolKey: "monetisation-ai" },
  { title: "Clip AI", url: "/app/clip-ai", logo: clipaiLogo, toolKey: "clipping-ai" },
  { title: "AskFint — AI Mentor", url: "/app/askfint", logo: askfintLogo },
  { title: "Keyword Research", url: "/app/keywords", logo: keywordResearchLogo, toolKey: "keyword-research" },
  { title: "Email List Builder", url: "/app/email-list-builder", logo: emailLogo, toolKey: "email-list-builder" },
  { title: "Sales Page Builder", url: "/app/sales-page-builder", logo: salesLogo, toolKey: "sales-page-builder" },
  { title: "Launch Advisor", url: "/app/launch-advisor", logo: launchLogo, toolKey: "launch-advisor" },
];

function SidebarCreditBadge({ isCollapsed }: { isCollapsed: boolean }) {
  const { balance, monthlyAllowance, isUnlimited, isLoading } = useCredits();
  const { subscription } = useAuth();
  if (isLoading) return null;

  const usedCredits = monthlyAllowance > 0 ? monthlyAllowance - balance : 0;
  const usagePercent = monthlyAllowance > 0 ? Math.round((usedCredits / monthlyAllowance) * 100) : 0;

  // Calculate reset date (1st of next month)
  const now = new Date();
  const resetDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const resetDateStr = resetDate.toLocaleDateString("en-US", { month: "long", day: "numeric" });

  const badge = (
    <Link to="/app" className="block px-4 py-2">
      <div className="px-3 py-2.5 rounded-lg bg-sidebar-accent/50 space-y-2">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-primary flex-shrink-0" />
          {!isCollapsed && (
            <span className="text-xs font-medium text-sidebar-foreground">
              {isUnlimited
                ? "Unlimited credits"
                : `${usedCredits.toLocaleString()} / ${monthlyAllowance.toLocaleString()} used`}
            </span>
          )}
        </div>
        {!isCollapsed && !isUnlimited && monthlyAllowance > 0 && (
          <Progress value={Math.min(usagePercent, 100)} className="h-1.5" />
        )}
      </div>
    </Link>
  );

  if (isCollapsed) return badge;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent side="right" className="max-w-[200px]">
        {isUnlimited ? (
          <p className="text-xs">You have unlimited credits on your plan.</p>
        ) : (
          <div className="space-y-1">
            <p className="text-xs font-medium">{balance.toLocaleString()} credits remaining</p>
            <p className="text-xs text-muted-foreground">Resets on {resetDateStr}</p>
            <Link to="/app" className="text-xs text-primary hover:underline block">See credit breakdown →</Link>
          </div>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

export function AppSidebar() {
  const location = useLocation();
  const { state } = useSidebar();
  const { user, subscription, signOut, isAdmin } = useAuth();
  const { planTier } = useCredits();
  const isCollapsed = state === "collapsed";
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);

  const userTier = (subscription.isLifetime ? "lifetime" : (planTier || subscription.planTier || "free")) as PlanTier | "lifetime";

  const handleManageSubscription = async () => {
    setIsLoadingPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.error === "no_customer") { toast.info(data.message || "No billing history found"); return; }
      if (data?.url) window.open(data.url, "_blank");
      else throw new Error("No portal URL returned");
    } catch (error: any) {
      console.error("Portal error:", error);
      toast.error(error.message || "Failed to open billing portal");
    } finally {
      setIsLoadingPortal(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
  };

  const NavItem = ({ item }: { item: { title: string; url: string; icon?: any; logo?: string; iconColor?: string; toolKey?: string } }) => {
    const isActive = location.pathname === item.url || location.pathname.startsWith(item.url + "/");
    const isLocked = item.toolKey ? !hasToolAccess(userTier, item.toolKey) : false;

    return (
      <SidebarMenuItem>
        <SidebarMenuButton asChild isActive={isActive}>
          <Link
            to={item.url}
            className={cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg transition-colors",
              isActive
                ? "bg-primary/15 text-primary font-medium border border-primary/20"
                : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
              isLocked && "opacity-60"
            )}
          >
            {item.logo ? (
              <img src={item.logo} alt={item.title} className="w-5 h-5 object-contain rounded" />
            ) : item.icon ? (
              <item.icon className={cn("w-5 h-5", item.iconColor)} />
            ) : null}
            {!isCollapsed && (
              <span className="flex-1 truncate">{item.title}</span>
            )}
            {!isCollapsed && isLocked && (
              <Lock className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            )}
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar className="border-r border-sidebar-border bg-sidebar">
      <SidebarHeader className="p-4 border-b border-sidebar-border">
        <Link to="/app" className="flex items-center gap-3">
          <img src={storycraftLogo} alt="StoryCraft" className="w-10 h-10 rounded-xl object-contain" />
          {!isCollapsed && (
            <div className="flex flex-col">
              <span className="font-display font-bold text-sidebar-foreground">StoryCraft</span>
              <span className="text-xs text-sidebar-foreground/60">Studio</span>
            </div>
          )}
        </Link>
      </SidebarHeader>

      <SidebarContent className="px-2 py-4">
        {/* Home */}
        <SidebarGroup>
          {!isCollapsed && <SidebarGroupLabel className="px-3 text-xs text-sidebar-foreground/50 uppercase tracking-wider mb-2">Home</SidebarGroupLabel>}
          <SidebarGroupContent>
            <SidebarMenu>
              {homeItems.map((item) => (
                <NavItem key={item.url} item={item} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Learn */}
        <SidebarGroup className="mt-6">
          {!isCollapsed && <SidebarGroupLabel className="px-3 text-xs text-sidebar-foreground/50 uppercase tracking-wider mb-2">Learn</SidebarGroupLabel>}
          <SidebarGroupContent>
            <SidebarMenu>
              {learnItems.map((item) => (
                <NavItem key={item.url} item={item} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Your Tools */}
        <SidebarGroup className="mt-6">
          {!isCollapsed && <SidebarGroupLabel className="px-3 text-xs text-sidebar-foreground/50 uppercase tracking-wider mb-2">Your Tools</SidebarGroupLabel>}
          <SidebarGroupContent>
            <SidebarMenu>
              {toolItems.map((item) => (
                <NavItem key={item.url} item={item} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Community — single link */}
        <SidebarGroup className="mt-6">
          <SidebarGroupContent>
            <SidebarMenu>
              <NavItem item={{ title: "Community", url: "/app/general", icon: Users, iconColor: "text-cyan-400" }} />
              <NavItem item={{ title: "Spin the Wheel", url: "/app/spin-wheel", icon: Gift, iconColor: "text-orange-400" }} />
              <NavItem item={{ title: "Support", url: "/app/support", icon: HelpCircle, iconColor: "text-emerald-400" }} />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarCreditBadge isCollapsed={isCollapsed} />
      <SidebarFooter className="p-4 border-t border-sidebar-border">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-3 w-full px-2 py-2 rounded-lg hover:bg-sidebar-accent transition-colors">
              <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center">
                <User className="w-4 h-4 text-secondary-foreground" />
              </div>
              {!isCollapsed && (
                <>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-sidebar-foreground truncate max-w-[120px]">
                      {user?.email?.split("@")[0] || "Member"}
                    </p>
                    <p className="text-xs text-sidebar-foreground/60">
                      {subscription.isLifetime ? "Lifetime" : subscription.planTier ? subscription.planTier.charAt(0).toUpperCase() + subscription.planTier.slice(1) : "Member"}
                    </p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-sidebar-foreground/60" />
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {isAdmin && (
              <>
                <DropdownMenuItem asChild>
                  <Link to="/app/admin" className="flex items-center">
                    <Shield className="w-4 h-4 mr-2" />
                    Admin Dashboard
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem asChild>
              <Link to="/app/settings" className="flex items-center">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/app/support" className="flex items-center">
                <HelpCircle className="w-4 h-4 mr-2" />
                Support
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleManageSubscription} disabled={isLoadingPortal}>
              <CreditCard className="w-4 h-4 mr-2" />
              {isLoadingPortal ? "Opening..." : "Manage Subscription"}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
              <LogOut className="w-4 h-4 mr-2" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
